import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Avatar } from 'ui';
import { cn } from 'utils/cn';

export interface NotifyWpFeedbackUser {
  label: string;
  value: number;
  checked: boolean;
  avatar?: string;
  email: string;
  name?: string;
  responsible: boolean;
  role?: string;
}

export type MentionDataUser = NotifyWpFeedbackUser & {
  type: 'user';
  node: 'user-link';
  isAI: boolean;
};

export interface MentionListProps {
  items: MentionDataUser[];
  command: (value: any) => void;
  onSelectAI?: () => void;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = forwardRef<MentionListRef, MentionListProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const scrollToRef = useRef<HTMLButtonElement>(null);

    // Group items by AI and TeamMembers
    const groupedItems = props.items.reduce((groups, item) => {
      const group = item.isAI ? 'Inner Circle' : 'Team Members';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(item);
      return groups;
    }, {} as Record<string, MentionDataUser[]>);

    // Flatten grouped items for navigation
    const flattenedItems = Object.values(groupedItems).flat();

    const selectItem = (index: number) => {
      const item = flattenedItems[index];

      if (item) {
        // Call the onSelectAI callback if the selected item is an AI user
        if (item.isAI && props.onSelectAI) {
          props.onSelectAI();
        }

        props.command(item);
      }
    };

    const upHandler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex(
        (selectedIndex + flattenedItems.length - 1) % flattenedItems.length
      );
    };

    const downHandler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedIndex((selectedIndex + 1) % flattenedItems.length);
    };

    const enterHandler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [props.items]);

    useEffect(() => {
      if (scrollToRef.current) {
        scrollToRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          upHandler(event);
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler(event);
          return true;
        }

        if (event.key === 'Enter') {
          enterHandler(event);
          return true;
        }

        return false;
      },
    }));

    return (
      <div className="flex flex-col gap-1 bg-background p-2 min-w-48 rounded-md shadow-md max-h-64 overflow-y-auto">
        {props.items.length ? (
          Object.entries(groupedItems).map(([groupName, groupItems]) => (
            <div key={groupName} className="flex flex-col gap-1">
              <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                {groupName}
              </div>
              {groupItems.map((item, groupIndex) => {
                const globalIndex = flattenedItems.findIndex(flattenedItem =>
                  flattenedItem.value === item.value && flattenedItem.isAI === item.isAI
                );
                return (
                  <button
                    ref={globalIndex === selectedIndex ? scrollToRef : null}
                    className={cn(
                      'flex items-center justify-between border-1 dark:hover:border-gray-2 dark:hover:bg-gray-2 w-full rounded-md p-1 text-left hover:border-border hover:bg-tertiary/5 border-transparent bg-transparent',
                      globalIndex === selectedIndex
                        ? 'bg-tertiary/5'
                        : 'border-transparent bg-transparent'
                    )}
                    key={`${groupName}-${groupIndex}`}
                    onClick={() => selectItem(globalIndex)}
                  >
                    <div className='flex gap-1'>
                      <Avatar
                        src={item.avatar}
                        title={item.label}
                        id={item.value}
                        size="sm"
                        className="bg-tertiary h-6 w-6"
                        colour="#fff" />
                      <span className='block'>
                        {item.label}
                      </span>
                    </div>
                    <span className='block text-sm text-gray-500 capitalize'>{item?.role}</span>
                  </button>
                );
              })}
            </div>
          ))
        ) : (
          <div className="item">No result</div>
        )}
      </div>
    );
  }
);

MentionList.displayName = 'MentionList';
export default MentionList;
